import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [settings, setSettings] = useState({
    logo: '/img/ЗНАЧЕК_КЦ.jpeg',
    siteName: 'Консультационный центр "Волшебная страна"',
    siteSubtitle: 'МАДОУ МО г. Краснодар "Детский сад № 213"',
  });
  const [pages, setPages] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    fetchPages();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/settings/public');
      setSettings(response.data);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get('/pages');
      setPages(response.data);
    } catch (error) {
      console.error('Ошибка загрузки страниц:', error);
    }
  };

  const staticMenuItems = [
    { label: 'Главная', path: '/' },
    { label: 'График работы', path: '/schedule' },
    { label: 'Документы', path: '/documents' },
    { label: 'Для родителей', path: '/consultations-list' },
    { label: 'Новости и события', path: '/events' },
    { label: 'Специалисты', path: '/specialists' },
    { label: 'Оставить заявку', path: '/request' },
    { label: 'Навигатор для родителей', path: '/useful-resources' }
  ];

  // Объединяем статические пункты меню и динамические страницы
  const menuItems = [
    ...staticMenuItems,
    ...pages.map(page => ({
      label: page.title,
      path: `/page/${page.slug}`,
    })),
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRequestClick = () => {
    navigate('/request');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleScroll = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      {settings.logo && (
        <Box
          component="img"
          src={settings.logo}
          alt="Логотип"
          sx={{
            height: 75,
            width: 75,
            margin: '6px auto',
            display: 'block',
          }}
        />
      )}
      <Typography variant="h6" sx={{ my: 2, fontWeight: 700, lineHeight: 1.3, px: 2, maxWidth: '100%', overflow: 'hidden', fontSize: { xs: '0.85rem', sm: '1.25rem' } }}>
        <Box component="span" sx={{ display: 'block', whiteSpace: { xs: 'normal', sm: 'nowrap' }, maxWidth: '100%' }}>
          Консультационный{'\u00A0'}центр
        </Box>
        <Box component="span" sx={{ display: 'block', whiteSpace: { xs: 'normal', sm: 'nowrap' }, maxWidth: '100%' }}>
          "Волшебная{'\u00A0'}страна"
        </Box>
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
        {settings.siteSubtitle}
      </Typography>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => {
                if (item.path.startsWith('#')) {
                  handleScroll(item.path);
                } else {
                  navigate(item.path);
                }
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Политика конфиденциальности как отдельный пункт */}
        <ListItem disablePadding sx={{ pl: 2 }}>
          <ListItemButton onClick={() => navigate('/privacy-policy')}>
            <ListItemText 
              primary="Политика конфиденциальности" 
              primaryTypographyProps={{ fontSize: '0.9rem', color: 'text.secondary' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              {settings.logo && (
                <Box
                  component="img"
                  src={settings.logo}
                  alt="Логотип"
                  sx={{
                    height: 75,
                    width: 75,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/')}
                />
              )}
              <Box>
                <Typography
                  variant="h6"
                  component={Link}
                  to="/"
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                    display: 'block',
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  <Box component="span" sx={{ display: 'block' }}>
                    Консультационный центр
                  </Box>
                  <Box component="span" sx={{ display: 'block' }}>
                    "Волшебная страна"
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  {settings.siteSubtitle?.replace(/Детский сад\s+№/g, 'Детский сад\u00A0№') || settings.siteSubtitle}
                </Typography>
              </Box>
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                {menuItems.map((item, index) => {
                  const menuItem = (() => {
                    // Добавляем подменю для "Оставить заявку"
                    if (item.label === 'Оставить заявку') {
                      return (
                        <Box
                          key={item.label}
                          onMouseEnter={() => setShowSubmenu(true)}
                          onMouseLeave={() => setShowSubmenu(false)}
                          sx={{ position: 'relative' }}
                        >
                          <Button
                            onClick={handleRequestClick}
                            endIcon={<ExpandMoreIcon />}
                            sx={{ 
                              color: 'text.primary',
                              fontSize: '0.8rem',
                              px: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          >
                            {item.label}
                          </Button>
                          {showSubmenu && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                mt: 0.5,
                                backgroundColor: 'white',
                                boxShadow: 3,
                                borderRadius: 1,
                                minWidth: 250,
                                zIndex: 1300,
                              }}
                            >
                              <Button
                                onClick={() => navigate('/privacy-policy')}
                                fullWidth
                                sx={{
                                  justifyContent: 'flex-start',
                                  px: 2,
                                  py: 1.5,
                                  color: 'text.primary',
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                              >
                                Политика конфиденциальности
                              </Button>
                            </Box>
                          )}
                        </Box>
                      );
                    }
                    
                    return (
                      <Button
                        key={item.label}
                        onClick={() => {
                          if (item.path.startsWith('#')) {
                            handleScroll(item.path);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        sx={{ 
                          color: 'text.primary',
                          fontSize: '0.8rem',
                          px: 1,
                        }}
                      >
                        {item.label}
                      </Button>
                    );
                  })();

                  return (
                    <React.Fragment key={item.label}>
                      {menuItem}
                      {index < menuItems.length - 1 && (
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.9rem',
                            mx: 0.5,
                            opacity: 0.4,
                          }}
                        >
                          |
                        </Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
